import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  formatNumber as formatNumberUtils,
  toNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';
import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

/**
 * Formatter for numeric values without unit display.
 * Handles null/undefined values by passing them through without formatting,
 * while numeric values are formatted with standard number formatting.
 * Does not display units - only the numeric value.
 *
 * @param {UnitValue} unitValue - The value and units to format
 * @param {any} unitValue.value - The value to format (can be any type)
 * @param {string} [unitValue.units] - The current units of the value (ignored in output)
 * @param {string} [displayUnits] - Optional target units to transform to before formatting (ignored in output)
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted value or original value if non-numeric
 *   - unitsLabel: Empty string (no units displayed)
 *   - valueUnitsLabel: Just the value with no units
 *
 * @example
 * formatNumber({ value: 'Active', units: '' })
 * // Returns: { valueLabel: 'Active', unitsLabel: '', valueUnitsLabel: 'Active' }
 *
 * @example
 * formatNumber({ value: null, units: 'status' })
 * // Returns: { valueLabel: '--', unitsLabel: '', valueUnitsLabel: '--' }
 *
 * @example
 * formatNumber({ value: 42, units: 'count' })
 * // Returns: { valueLabel: '42', unitsLabel: '', valueUnitsLabel: '42' }
 */
export const formatNumber = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  const transformed = transformUnits(unitValue, displayUnits);
  const value = toNumber(transformed.value);

  if (isNullOrUndefined(value)) {
    return unitValueToFormattedUnitLabels({ value: transformed.value, units: undefined });
  }

  return unitValueToFormattedUnitLabels({
    value: formatNumberUtils(transformed.value),
    units: undefined,
  });
};
